import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { INVOICE_STATUSES } from '@naya/shared'
import { AdminLayout } from '../components/AdminLayout.js'
import { InvoiceBadge } from '../components/InvoiceBadge.js'
import { useInvoicesList } from '../hooks/useInvoices.js'
import { fmtDate, fmtMoney } from '../format.js'
import type { InvoiceStatus } from '../types.js'

/**
 * Standalone cross-tenant invoice list at /admin/invoices. Status filter +
 * optional tenant filter (pre-filled when arrived at via ?tenant_id=). Rows
 * link through to the invoice detail page.
 */
export function InvoiceList() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tenantFromUrl = searchParams.get('tenant_id') ?? ''

  const [status, setStatus] = useState<InvoiceStatus | ''>('')
  const [tenantId, setTenantId] = useState(tenantFromUrl)

  const query = useInvoicesList({
    status,
    ...(tenantId ? { tenantId } : {}),
  })
  const invoices = query.data?.invoices ?? []

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <h1>Invoices</h1>
      </div>

      <div className="admin-filters">
        <select
          className="admin-select"
          aria-label="Filter by status"
          value={status}
          onChange={(e) =>
            setStatus(e.target.value as InvoiceStatus | '')
          }
        >
          <option value="">All statuses</option>
          {INVOICE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s[0]!.toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        <input
          className="admin-input"
          placeholder="Tenant ID"
          aria-label="Filter by tenant ID"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
        />
      </div>

      {query.isLoading ? (
        <p className="admin-audit-meta">Loading invoices…</p>
      ) : query.isError ? (
        <div className="admin-error-state">
          <p>Couldn't load invoices.</p>
          <button
            type="button"
            className="admin-btn"
            onClick={() => void query.refetch()}
          >
            Retry
          </button>
        </div>
      ) : invoices.length === 0 ? (
        <div className="admin-card">
          <div className="admin-empty">No invoices match these filters.</div>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="admin-card" style={{ padding: 0 }}>
            <div className="admin-table-wrap admin-table-wrap--has-cards">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Tenant</th>
                    <th>Period</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr
                      key={inv.id}
                      onClick={() =>
                        navigate(`/admin/invoices/${inv.id}`)
                      }
                    >
                      <td>{inv.invoice_number}</td>
                      <td>{inv.tenant_name}</td>
                      <td className="admin-audit-meta">
                        {fmtDate(inv.period_start)} –{' '}
                        {fmtDate(inv.period_end)}
                      </td>
                      <td>{fmtMoney(inv.amount_centavos)}</td>
                      <td>
                        <InvoiceBadge status={inv.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="admin-tenant-cards">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="admin-tenant-card"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    navigate(`/admin/invoices/${inv.id}`)
                  }
                }}
              >
                <div className="admin-tenant-card-top">
                  <span className="admin-tenant-card-name">
                    {inv.invoice_number}
                  </span>
                  <InvoiceBadge status={inv.status} />
                </div>
                <div className="admin-tenant-card-meta">
                  <span>{inv.tenant_name}</span>
                  <span>·</span>
                  <span>{fmtMoney(inv.amount_centavos)}</span>
                </div>
                <div className="admin-tenant-card-meta">
                  <span>
                    {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </AdminLayout>
  )
}
